import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useWizard, WizardStepLayout } from 'components/layout/wizards';
import { SelectField, BaseTextInput, BaseTextArea, InputLabel } from 'components/forms';
import { Button, PrimaryButton } from 'components/buttons';
import { testWorkerUrl } from 'store/slices/workersSlice';
import { AlertSuccess, AlertError } from 'components/layout/alerts';
import { WORKER_PARSER_METHODS, WORKER_PARSER_METHODS_OPTIONS } from 'constants/workers';

export function ParserUrlForm({
  initialValues = {},
  onSubmit,
  onCancel,
  isWizard = false,
  saving = false
}) {
  const dispatch = useDispatch();
  const [method, setMethod] = useState(initialValues.method || WORKER_PARSER_METHODS.GET);
  const [url, setUrl] = useState(initialValues.url || '');
  const [headers, setHeaders] = useState(
    typeof initialValues.headers === 'object'
      ? JSON.stringify(initialValues.headers, null, 2)
      : (initialValues.headers || '')
  );
  const [body, setBody] = useState(
    typeof initialValues.body === 'object'
      ? JSON.stringify(initialValues.body, null, 2)
      : (initialValues.body || '')
  );

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    setTestResult(null);
  }, [method, url, headers, body]);

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      let parsedHeaders = {};
      let parsedBody = {};

      if (headers) {
        try {
          parsedHeaders = JSON.parse(headers);
        } catch (_e) {
          throw { error: 'Invalid JSON in Headers' };
        }
      }

      if (body) {
        try {
          parsedBody = JSON.parse(body);
        } catch (_e) {
          throw { error: 'Invalid JSON in Body' };
        }
      }

      const payload = {
        url,
        method,
        headers: parsedHeaders,
        body: parsedBody
      };

      const response = await dispatch(testWorkerUrl(payload)).unwrap();
      setTestResult(response.data || { success: true });
    } catch (e) {
      setError(e.error || e.message || 'Failed to test URL');
    } finally {
      setTesting(false);
    }
  };

  const handleProceed = () => {
    let parsedHeaders = {};
    let parsedBody = null;
    try {
      if (headers) parsedHeaders = JSON.parse(headers);
      if (method === WORKER_PARSER_METHODS.POST && body) parsedBody = JSON.parse(body);
    } catch (_e) {
      // Should be caught by test
    }

    onSubmit({ method, url, headers: parsedHeaders, body: parsedBody, testResult });
  };

  const formContent = (
    <div className="space-y-6">
      {
        (testResult || error) && (
          <div className='mb-8'>
            {testResult && (
              <AlertSuccess title="Test Successful">
                <div className="flex flex-col gap-1">
                  <span>Response received. You can proceed.</span>
                  <div className="flex gap-4 text-xs mt-1 font-mono">
                    {testResult.statusCode && <span>Status: {testResult.statusCode}</span>}
                    {testResult.duration && <span>Time: {testResult.duration}</span>}
                    {testResult.size && <span>Size: {testResult.size} bytes</span>}
                  </div>
                </div>
              </AlertSuccess>
            )}

            {error && (
              <AlertError title="Test Failed">
                {error}
              </AlertError>
            )}
          </div>
        )
      }

      <div className="flex gap-4">
        <div className="w-1/4">
          <SelectField
            label="Method"
            value={method}
            onChange={setMethod}
            options={WORKER_PARSER_METHODS_OPTIONS}
          />
        </div>
        <div className="w-3/4">
          <InputLabel htmlFor="url">URL</InputLabel>
          <div className="mt-2">
            <BaseTextInput
              id="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/api/data"
            />
          </div>
        </div>
      </div>

      <div>
        <InputLabel htmlFor="headers">Headers (JSON)</InputLabel>
        <div className="mt-2">
          <BaseTextArea
            id="headers"
            value={headers}
            onChange={(e) => setHeaders(e.target.value)}
            placeholder='{"Authorization": "Bearer token"}'
            rows={3}
          />
        </div>
      </div>

      {method === 'POST' && (
        <div>
          <InputLabel htmlFor="body">Body (JSON)</InputLabel>
          <div className="mt-2">
            <BaseTextArea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder='{"key": "value"}'
              rows={5}
            />
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={handleTest} disabled={!url || testing}>
          {testing ? 'Testing...' : 'Test URL'}
        </Button>
      </div>
    </div>
  );

  if (isWizard) {
    return (
      <WizardStepLayout
        title="Configure Parser URL"
        description="Enter the URL and request details for the parser."
        primaryAction={{
          label: 'Next',
          onClick: handleProceed,
          disabled: !testResult
        }}
      >
        {formContent}
      </WizardStepLayout>
    );
  }

  return (
    <div>
      {formContent}
      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-700 relative z-20">
        <Button onClick={onCancel} disabled={testing}>Cancel</Button>
        <PrimaryButton onClick={handleProceed} disabled={!testResult || testing} saving={saving}>
          Save Changes
        </PrimaryButton>
      </div>
    </div>
  );
}

export default function ParserUrlStep() {
  const { nextStep, data } = useWizard();
  return (
    <ParserUrlForm
      initialValues={data}
      onSubmit={nextStep}
      isWizard={true}
    />
  );
}
